import { ReactElement, useCallback, useEffect, useState } from "react";
import Select from "react-select"; 
import { getTimeString } from "../../utils/time";
import { getNextKey, getPreviousKey, getFirstKey, getLastKey } from "../../utils/object";
import { hasPermission } from "../common/ProtectedRoute";
import SidebarLayout from "../common/SidebarLayout";
import useApiState, { api, ApiStateSetter } from "../../hooks/use-api-state";
import { IAppInfo, IUserInfo } from "../../messages/IAppInfo";
import Loading from "../common/Loading";
import ErrorNotification from "../common/ErrorNotification";
import styles from "./Content.module.css";
import { IChapter, IContentData, IContentStructure, IGetUserContentResponse, ILastModuleVisited, 
         PRESENTATION, CODEREVIEW, ASSESSMENT, IModule,
         ISlide, SlideVisitedStatisticsDict, VisitedSlides, 
         LabAccomplishment} from "../../messages/IGetUserContentResponse";
import { useSearchParams } from "react-router-dom";
import ContentSlides from "./ContentSlides";
import CodeReview from "./CodeReview";
import Assessment from "./Assessment";
import { findQuestionIdByLabId, IAssessmentObject, IQuestion, IAnswersData, IAssessmentResponse } from "../../messages/IAssessments";
import ContentItemSelector from "./ContentLessonSelector";


export type LessonKey = {
  chapterKey: string,
  moduleKey: string,
};

type SelectedCourse = {
  courseIndex: number,
  contentData: IContentData,
};

type SelectedItem = {
  lessonKey: LessonKey,
  type: string,
  chapter: IChapter,
  // If it is a presentation
  modulePath?: string,
  slides?: { [slideIndex: string]: ISlide },
  module?: IModule,
  // If it is a code review, lines and correct are in the module
  // If it is assessment, we have an assessment attribute
  assessment?: string,
};

const ELAPSED_TEXT_TOP = 30;

export default function Content(props: {
  appInfo: IAppInfo,
  userInfo: IUserInfo,
  codeReviewOnly: boolean,
}): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();

  const [apiState, setApiState] = useApiState(true);
  const setUnusedApiState = useApiState(true)[1];
  const [userContent, setUserContent] = useState<IGetUserContentResponse | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<SelectedCourse | null>(null);
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);
  const [labAccomplishment, setLabAccomplishment] = useState<LabAccomplishment | null>(null); // Used for code review
  const [message, setMessage] = useState<string | null>(null);
  const [codeReviewMessage, setCodeReviewMessage] = useState<string | null>(null);

  const [getAssessmentApiState, setGetAssessmentApiState] = useApiState(false);
  const [assessment, setAssessment] = useState<IAssessmentResponse | null>(null);
  const [assessmentObject, setAssessmentObject] = useState<IAssessmentObject | null>(null);
  const [questions, setQuestions] = useState<IQuestion[] | null>(null);
  const [answersData, setAnswersData] = useState<IAnswersData | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [elapsed, setElapsed] = useState<number | null>(null); // In seconds (or null if no counter)

  useEffect(() => {
    const handleScroll = () => {
      const elapsedDivElement = document.getElementById("elapsedDiv");
      const elapsedTextElement = document.getElementById("elapsedText");
      if (elapsedDivElement && elapsedTextElement) {
        const rect = elapsedDivElement.getBoundingClientRect();
        if (rect.top<0) {
          elapsedTextElement.style.top = `${ELAPSED_TEXT_TOP}px`;
          elapsedTextElement.style.position = "fixed";
        }
        else {
          elapsedTextElement.style.top = "auto";
          elapsedTextElement.style.position = "static";
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.title = props.codeReviewOnly ? "Code Review" : "Courses";
  }, [props.codeReviewOnly]);

  function connectToSqs() {
    sqsutils.init("", 20, props.appInfo.csrf_token,
      () => {}, // connectedCallback
      (immediateReconnect) => {}, // disconnectedCallback
      () => {}, // statusChangeCallback
      (error)=>{
        setMessage("Error connecting to the server. Please try again later.");
      }, // giveupCallback
      false);
    sqsutils.sqs_connect(props.userInfo.user_id, "ASSESSMENT", false);
  }


  function listenOnSqs() {
    sqsutils.registerChannelProcessor("ASSESSMENT", (json: SQSMessage) => {
      if (json.cmd==="labstarted" || json.cmd==="labfinished") {
        const questionId = findQuestionIdByLabId(questions, json.labid);
        if (questionId) {
          let _answersData = answersData;
          if (!_answersData)
            _answersData = { key:"", answers: {}, submitted: false, passed: false, can_restart_at: 0, can_restart_at_text: "", results: {} };
          if (!_answersData.answers)
            _answersData.answers = {};
          _answersData.answers[questionId] = json.cmd==="labstarted" ? { "doing": true } : { "finished": true };
          setAnswersData(_answersData);
        }
      }
      else if (json.cmd==="message") {
        if (json.message) 
          setMessage(json.message);
      }
      else if (json.cmd==="reload") {
        if (json.message) 
          setMessage(json.message);
        if (json.key && json.qualification_certificate_link) {
          const assessmentKey = json.key;
          const [cohort_id, lmodule_code, questions_id] = assessmentKey.split("/");
          const qualification_certificate_link = json.qualification_certificate_link;
          if (qualification_certificate_link) {
            if (userContent?.content_data) {
              for (let content of userContent?.content_data) {
                let contentStructure = content.content_structure;
                if (contentStructure.cohort_id === cohort_id && contentStructure.lmodule_code === lmodule_code) {
                  const assessment = contentStructure.assessments?.[questions_id];
                  if (assessment) {
                      // Make a copy, set qualification_certificate_link to that in json.
                      const updatedAssessment: IAssessmentObject = {
                        ...assessment,
                        qualified: true,
                        qualification_certificate_link: json.qualification_certificate_link
                      };
                      setAssessmentObject(updatedAssessment);
                  }
                }
              }
            }
          }
        }
      }
    });
  }

  function handleChange(selectedOption: any) {
    let sp = new URLSearchParams();
    sp.set("course-id", selectedOption.value);
    setSearchParams(sp, { replace: true });
    if (userContent) {
      /*
      const selectedCourseIndex = selectedOption.value;
      setSelectedCourse({
        courseIndex: selectedCourseIndex,
        contentData: userContent.content_data[selectedCourseIndex],
      });
      */
    }
  }

  const checkIfModuleIsFinishedByTheUser = useCallback((userContent: IGetUserContentResponse, contentKey: string, moduleKey: string) => {
    let ret = false;
    const visitedSlides = userContent.visitedslides;
    if (!!visitedSlides && !!visitedSlides[contentKey]) {
      const visitedModuleStatistics = visitedSlides[contentKey][moduleKey] as SlideVisitedStatisticsDict;
      if (!!visitedModuleStatistics) {
        const visitedEnd = visitedModuleStatistics["_END"];

        if (typeof visitedEnd === 'number') {
          ret = visitedEnd > 0;
        } else if (typeof visitedEnd === 'object') {
          ret = (visitedEnd.visited ?? 0) > 0;
        }
      }
    }

    return ret;
  }, []);


  const calculateModuleFinished = useCallback((userContent: IGetUserContentResponse, contentKey: string, chapterId: string, modules: { [moduleKey: string]: IModule }) => {
    let finishedCount = 0;
    if (modules) {
      for (let module of Object.values(modules)) {
        const moduleId = module.id ?? "";
        const moduleKey = `${chapterId}/${moduleId}`;
        if (checkIfModuleIsFinishedByTheUser(userContent, contentKey, moduleKey)) {
          finishedCount += 1;
        }
      }
    }
    return finishedCount;
  }, [checkIfModuleIsFinishedByTheUser]);


  const recalculateChapterFinished = useCallback((userContent: IGetUserContentResponse) => {
    for (let content of userContent.content_data) {
      let contentStructure = content.content_structure;
      const cohortCode = contentStructure.content_cohort_code ?? "";
      const contentId = contentStructure.id ?? "";
      const contentKey = `${cohortCode}/${contentId}`;
      if (!!contentStructure && !!contentStructure.chapters) {
        for (let chapter of Object.values(contentStructure.chapters)) {
          const type = chapter.type || PRESENTATION;
          let finishedCount = 0;
          if (type.startsWith(PRESENTATION))
            finishedCount = calculateModuleFinished(userContent, contentKey, chapter.id, chapter.modules)
          chapter.done_count = finishedCount;
        }
      }
    }
  }, [calculateModuleFinished]);


  function findCourseIndexByContentKey(userContent: IGetUserContentResponse, lastContentVisited: string) {
    let contentDataIndex = 0;
    for (const contentData of userContent.content_data) {
      const contentKey = `${contentData.content_structure.content_cohort_code}/${contentData.content_structure.id}`;
      if (contentKey === lastContentVisited) {
        return contentDataIndex;
      }
      contentDataIndex += 1;
    }
    return null;
  }

  
  const loadContentData = useCallback(async () => {
    try {
      let userContent = await api.getUserContent(
        setApiState,
        props.userInfo.user_id,
      );
      if (!!userContent) {
        recalculateChapterFinished(userContent);
      }
      setUserContent(userContent);

    } catch (e) {
    }
  }, [props.userInfo.user_id, setApiState, recalculateChapterFinished]);


  useEffect(() => {
    loadContentData();
    connectToSqs();
  }, [loadContentData]);



  
  const getChapterAndIndexFromId = useCallback((contentStructure: IContentStructure, chapterId: string): [IChapter, string] | null => {
    if (!contentStructure.chapters) {
      return null;
    }

    for (const [index, chapter] of Object.entries(contentStructure.chapters)) {
      if (chapter.id === chapterId) {
        return [chapter, index];
      }
    }

    return null;
  }, []);

  const getModuleAndIndexFromId = useCallback((chapter: IChapter, moduleId?: string): [IModule, string] | null => {
    if (!chapter.modules) {
      return null;
    }
    for (const [index, module] of Object.entries(chapter.modules)) {
      if (module.id === moduleId) {
        return [module, index];
      }
    }

    return null;
  }, []);


  const getNextChapter = useCallback((chapterKey: string) => {
    let chapters = selectedCourse?.contentData?.content_structure?.chapters || null;
    const nextChapterKey = getNextKey(chapters, chapterKey);
    return (!!chapters && !!nextChapterKey) ? chapters[nextChapterKey] : null; 
  }, [selectedCourse?.contentData?.content_structure?.chapters]);


  const getPreviousChapter = useCallback((chapterKey: string): IChapter | null => {
    let chapters = selectedCourse?.contentData?.content_structure?.chapters || null;
    const previousChapterKey = getPreviousKey(chapters, chapterKey);
    return (!!chapters && !!previousChapterKey) ? chapters[previousChapterKey] : null;
  }, [selectedCourse?.contentData?.content_structure?.chapters]);


  const getNextLesson = useCallback((chapterKey: string, moduleKey: string) => {
    let chapters = selectedCourse?.contentData?.content_structure?.chapters;
    if (!chapters) {
      return null;
    }
    const chapter = chapters[chapterKey];
    const modules = chapter?.modules;
    const nextModuleKey = getNextKey(modules, moduleKey);
    if (nextModuleKey) {
      return { chapter: chapter, module: modules[nextModuleKey] };
    } else {
      let nextChapter = getNextChapter(chapterKey);
      const nextModuleKey = getFirstKey(nextChapter?.modules);
      return nextModuleKey ? { chapter: nextChapter, module: nextChapter?.modules[nextModuleKey] } : null;
    }
  }, [getNextChapter, selectedCourse?.contentData?.content_structure?.chapters]);


  const getPreviousLesson = useCallback((chapterKey: string, moduleKey: string) => {
    let chapters = selectedCourse?.contentData?.content_structure?.chapters;
    if (!chapters) {
      return null;
    }
    const chapter = chapters[chapterKey];
    const modules = chapter.modules;
    const previousModuleKey = getPreviousKey(modules, moduleKey);
    if (previousModuleKey) {
      return { chapter: chapter, module: modules[previousModuleKey] };
    }
    else {
      let previousChapter = getPreviousChapter(chapterKey);
      const previousModuleKey = getLastKey(previousChapter?.modules);
      return previousModuleKey ? { chapter: previousChapter, module: previousChapter?.modules[previousModuleKey] } : null;
    }
  }, [getPreviousChapter, selectedCourse?.contentData?.content_structure?.chapters]);


  function computePreviousLessonLink(lesson: LessonKey) {
    const previousLesson = getPreviousLesson(lesson.chapterKey, lesson.moduleKey);
    let { chapter, module } = previousLesson || { chapter: null, module: null };
    if (!chapter || !module) {
      return null; // No previous lesson available
    }
    const sp = new URLSearchParams(searchParams);
    sp.set("chapter-id", chapter.id);
    if (module?.id)
      sp.set("lesson-id", module?.id);
    else
      sp.delete("lesson-id");
    return `?${sp.toString()}`;
  }




  function computeNextLessonLink(lesson: LessonKey) {
    const nextLesson = getNextLesson(lesson.chapterKey, lesson.moduleKey);
    let { chapter, module } = nextLesson || { chapter: null, module: null };
    if (!chapter || !module) {
      return null; // No next lesson available
    }
    const sp = new URLSearchParams(searchParams);
    sp.set("chapter-id", chapter.id);
    if (module?.id)
      sp.set("lesson-id", module.id);
    else
      sp.delete("lesson-id");
    return `?${sp.toString()}`;
  }

  const getLastVisitedLessonId = useCallback((contentData: IContentData, visitedSlides: VisitedSlides) => {
    const contentKey = `${contentData.content_structure.content_cohort_code ?? ""}/${contentData.content_structure.id}`;
    const visitedSlidesOfContent = visitedSlides[contentKey];
    if (!visitedSlidesOfContent) {
      return null;
    }

    const lastModuleVisited = visitedSlidesOfContent["_last_module_visited"] as ILastModuleVisited;
    if (!!lastModuleVisited && !!contentData.content_structure.chapters) {
      for (const [chapterKey, chapter] of Object.entries(contentData.content_structure.chapters)) {
        for (const [moduleKey, module] of Object.entries(chapter.modules)) {
          const moduleId = `${chapter.id}/${module.id}`;
          if (moduleId === lastModuleVisited.module_key) { //lastModuleVisited is saved with chapterId/moduleId as the module_key
            if (lastModuleVisited.slide_id === "_END") {
              const nextLesson = getNextLesson(chapterKey, moduleKey);
              if (!!nextLesson) {
                return { chapterId: nextLesson.chapter?.id, moduleId: nextLesson.module?.id };
              }
            }
            return { chapterId: chapter.id, moduleId: module.id };
          }
        }
      }
    }
    return null;
  }, [getNextLesson]);


  useEffect(() => {
    if (userContent !== null && userContent.content_data.length > 0) {
      const courseId = searchParams.get("course-id");
      if (courseId === null || +courseId >= userContent.content_data.length || +courseId < 0) {
        // navigate to the last visited course
        let courseIndex = findCourseIndexByContentKey(userContent, userContent.last_content_visited);
        if (courseIndex === null) {
          courseIndex = 0;
        }
        let sp = new URLSearchParams(searchParams);
        sp.set("course-id", courseIndex.toString());
        sp.delete("chapter-id");
        sp.delete("lesson-id");
        setSearchParams(sp, { replace: true });
        //setSelectedLesson(null);
      } else {
        let selectedCourseIndex = +courseId;
        setSelectedCourse({
          courseIndex: selectedCourseIndex,
          contentData: userContent.content_data[selectedCourseIndex],
        });
      }
      listenOnSqs();
    }
  }, [userContent, searchParams, setSearchParams, setSelectedCourse, getChapterAndIndexFromId, getModuleAndIndexFromId, getLastVisitedLessonId]);


  function getModule(chapterId: string, moduleId?: string) {
    if ( userContent != null && selectedCourse != null) {
      const selectedCourseIndex = selectedCourse.courseIndex;
      const contentData = userContent.content_data[selectedCourseIndex];
      const chapterAndIndex = getChapterAndIndexFromId(contentData.content_structure, chapterId);
      if (!!chapterAndIndex) {
        const [chapter, chapterKey]: [IChapter, string] = chapterAndIndex;
        const chapterType = chapter.type || PRESENTATION;
        if (chapterType.startsWith(ASSESSMENT)) {
          return null; // Assessment chapters do not have modules;
        }
        else {
          const moduleAndIndex = getModuleAndIndexFromId(chapter, moduleId);
          if (!!moduleAndIndex) {
            const [module, moduleKey] = moduleAndIndex;
            return module;
          }
        }
      }
    }
    return null;
  }

  useEffect(() => {
    if ( userContent !== null && userContent.content_data.length > 0 &&
         selectedCourse !== null)
    {
      // Course has been selected
      let selectedCourseIndex = selectedCourse?.courseIndex;
      const contentData = userContent.content_data[selectedCourseIndex];

      const chapterId = searchParams.get("chapter-id");
      const moduleId = searchParams.get("lesson-id");

      if ( chapterId !== null &&
           !!contentData.content_structure.chapters) {
        // there is lesson selected, so it should be loaded
        const chapterAndIndex = getChapterAndIndexFromId(contentData.content_structure, chapterId);
        if (!!chapterAndIndex) {
          const [chapter, chapterKey]: [IChapter, string] = chapterAndIndex;
          const chapterType = chapter.type || PRESENTATION;
          if (chapterType.startsWith(ASSESSMENT)) {
            if (chapter.assessment)
              setSelectedItem({
                lessonKey: {
                  chapterKey: chapterKey,
                  moduleKey: "",
                },
                type: chapterType,
                chapter: chapter,
                assessment: chapter.assessment,
              });
            else
              setSelectedItem(null);
            return;
          }
          else if (moduleId!==null) {
            const moduleAndIndex = getModuleAndIndexFromId(chapter, moduleId);
            if (!!moduleAndIndex) {
              const [module, moduleKey] = moduleAndIndex;
              const moduleType = module.type || PRESENTATION;
              /*
              if (module.disabled) {
                const message = selectedCourse?.contentData?.content_structure?.enable_lesson_text ?? "This lesson is not available yet.";
                setSelectedItem(null);
                setMessage(message);
                return;
              }
              */
              //
              if (moduleType.startsWith(PRESENTATION) && !!module.slides) {
                const moduleDir = module.dir || "";
                const dir = `${contentData.content_structure.dir}/${chapter.dir}/${moduleDir}`;
                const modulePath = contentData.content_root + encodeURIComponent(dir) + "/index.html";
                setMessage(null);
                setSelectedItem({
                  lessonKey: {
                    chapterKey: chapterKey,
                    moduleKey: moduleKey,
                  },
                  type: moduleType,
                  chapter: chapter,
                  module: module,
                  modulePath: modulePath,
                  slides: module.slides,
                });
              }
              else if (moduleType.startsWith(CODEREVIEW) && !!module.lines) {
                if (selectedItem?.lessonKey.chapterKey !== chapterKey || selectedItem?.lessonKey.moduleKey !== moduleKey) {
                    setCodeReviewMessage(null);
                }
                setSelectedItem({
                  lessonKey: {
                    chapterKey: chapterKey,
                    moduleKey: moduleKey,
                  },
                  type: moduleType,
                  chapter: chapter,
                  module: module,
                })
              }
              return;
            }
          }
        }
      } else {
        // navigate to the last visited lesson
        let sp = new URLSearchParams(searchParams);
        const lessonId = getLastVisitedLessonId(userContent.content_data[selectedCourseIndex], userContent.visitedslides);
        if (!!lessonId && !!lessonId.chapterId && !!lessonId.moduleId) {
          sp.set("chapter-id", lessonId.chapterId);
          sp.set("lesson-id", lessonId.moduleId);
        } else {
          sp.delete("chapter-id");
          sp.delete("lesson-id");
        }
        setSearchParams(sp, { replace: true });
        setSelectedItem(null);
        return;
      }

      let sp = new URLSearchParams(searchParams);
      sp.delete("chapter-id");
      sp.delete("lesson-id");
      setSearchParams(sp, { replace: true });
      setSelectedItem(null);
    }
  }, [selectedCourse]);


  useEffect(() => {
    if (selectedCourse && selectedItem) {
      const cohortCode = (selectedCourse.contentData.content_structure.content_cohort_code ?? "").replaceAll("/", "_");
      const contentId = selectedCourse.contentData.content_structure.id.replaceAll("/", "_");
      const type = selectedItem.type || PRESENTATION;
      const chapterId = !!selectedItem.chapter ? selectedItem.chapter.id.replaceAll("/", "_") : "";

      const moduleId = selectedItem?.module?.id.replaceAll("/", "_");
      const module = getModule(chapterId, moduleId);
      if (module?.disabled) {
        const message = selectedCourse?.contentData?.content_structure?.enable_lesson_text ?? "This lesson is not available yet.";
        /*setSelectedItem(null);*/
        setMessage(message);
        return;
      }

      if (type.startsWith(ASSESSMENT) && selectedItem.assessment) {
        /*
        const cohort_id = selectedCourse.contentData.content_structure.cohort_id ?? "";
        const lmodule_code = selectedCourse.contentData.content_structure.lmodule_code ?? "";
        const assessments = selectedCourse.contentData.content_structure.assessments ?? {};
        const assessment = assessments[selectedItem.assessment] ?? {};
        const questions_id = assessment.questions_id;
        getAssessment(cohort_id, lmodule_code, questions_id);
        */
      } else if (type.startsWith(CODEREVIEW)) {
        const moduleId = selectedItem?.module?.id.replaceAll("/", "_");
        const module = getModule(chapterId, moduleId);
        if (userContent && module) {
          //let labId = `${cohortCode}/${contentId}/${chapterId}/${moduleId}`;
          let labId = `codereview:${moduleId}`;
          let labAccomplishment = userContent.lab_accomplishments[labId];
          if (labAccomplishment) {
            let lastAccomplishmentTime = Math.max(...Object.keys(labAccomplishment).map(Number));
            if (lastAccomplishmentTime>0) {
              let lastAccomplishment = labAccomplishment[lastAccomplishmentTime];
              lastAccomplishment.started = lastAccomplishmentTime; // Add the started time to the object
              setLabAccomplishment(lastAccomplishment);
              return;
            }
          }
          setLabAccomplishment(null);
        }
      }
    }
  }, [selectedItem]);


  async function CodeReviewSubmitHandler(
      index: number,
      /*setMessage: ((message: string | null) => void) | null,*/
      setSubmitApiState: ApiStateSetter)
  {
    if (!selectedCourse || !selectedItem) {
      return;
    }
    const cohortCode = (selectedCourse.contentData.content_structure.content_cohort_code ?? "").replaceAll("/", "_");
    const lmoduleCode = (selectedCourse.contentData.content_structure.lmodule_code ?? "").replaceAll("/", "_");
    const contentId = selectedCourse.contentData.content_structure.id.replaceAll("/", "_");
    const contentKey = cohortCode + "/" + contentId
    const chapterId = !!selectedItem.chapter ? selectedItem.chapter.id.replaceAll("/", "_") : "";
    const moduleId = selectedItem?.module?.id.replaceAll("/", "_");
    const moduleKey = chapterId + "/" + moduleId;
    const challengeId = selectedItem?.module?.challenge_id ?? null;

    //let labId = `${cohortCode}/${contentId}/${chapterId}/${moduleId}`;
    let labId = `codereview:${moduleId}`;

    if (contentId && moduleId) {
      try {
        const resp = await api.submitCodeReview(
          setSubmitApiState,
          cohortCode,
          lmoduleCode,
          contentId,
          chapterId,
          moduleId,
          challengeId,
          index,
        );
        if (index>=0) { // Calling this with index=-1 means that we want to redo the challenge (remove lab accomplishment)
          let message = null; // We cheat by adding the message to the lab accomplishment later, to pass it on to the CodeReview component
          if (resp.status==="correct" || resp.status==="partial") {
            slideVisited(chapterId, moduleId, "_END", true, false); // This marks the whole module to be completed ("_END" is a special slide id for the last slide)
            message = "Yes, that is correct!";
          }
          else if (resp.status==="failed") {
            message = "That was not the correct line."
            slideVisited(chapterId, moduleId, "_END", false, false);
          }
          if (resp.message) { // If there is a message, override the default
            message = resp.message;
          }
          if (/*setMessage &&*/ message) {
            /*setMessage(message);*/
            setCodeReviewMessage(message);
          }
          
          if (resp.lab_accomplishment) {
            if (userContent) {
              let started = resp.started;
              if (!userContent.lab_accomplishments[labId]) {
                userContent.lab_accomplishments[labId] = {};
              }
              userContent.lab_accomplishments[labId][started] = resp.lab_accomplishment;
              setLabAccomplishment(resp.lab_accomplishment);
              setUserContent(userContent);
            }
          }
        }
        else {
          if (userContent) {
            userContent.lab_accomplishments[labId] = {};
            setUserContent(userContent);
          }
          setLabAccomplishment(null);
          markSlideVisited(contentKey, moduleKey, "_END", false);
        }
      } catch (e: Response | any) {
        console.error("Error submitting code review:", e);
      }
    }
  }
  
  function markSlideVisited(contentKey: string, moduleKey: string, slideId: string, visited: boolean) {
    setUserContent((prev: IGetUserContentResponse | null) => {
      if (!prev) {
        return null;
      }

      let newState = JSON.parse(JSON.stringify(prev)) as IGetUserContentResponse;

      if (!newState.visitedslides[contentKey]) {
        newState.visitedslides[contentKey] = {}
      }
      if (!newState.visitedslides[contentKey][moduleKey]) {
        newState.visitedslides[contentKey][moduleKey] = {}
      }

      let slideVisitedStatisticsDict = newState.visitedslides[contentKey][moduleKey] as SlideVisitedStatisticsDict;
      slideVisitedStatisticsDict[slideId] = visited ? 1 : 0; // Non-zero just to indicate accomplishment

      // Mark last visited slide in local structure (REST API already updated the DB / persistence)
      newState.visitedslides[contentKey]["_last_module_visited"] = { "module_key": moduleKey, "slide_id": slideId };

      recalculateChapterFinished(newState);

      return newState;
    });
  }

  async function slideVisitedHandler(chapterId:string, moduleId:string, slideId: string, visited: boolean) {
    slideVisited(chapterId, moduleId, slideId, visited, true);
  }


  async function slideVisited(chapterId:string, moduleId:string, slideId: string, visited: boolean, setCodeReview:boolean) {
    if (!selectedCourse) {
      return;
    }

    if (visited===false) {
      setCodeReviewMessage(null);
      if (!hasPermission(props.userInfo, [{permission: "test"}])) {
        // Only allow to unvisit if the user has the "test" permission
        return;
      }
    }

    const cohortCode = (selectedCourse.contentData.content_structure.content_cohort_code ?? "").replaceAll("/", "_");
    const contentId = selectedCourse.contentData.content_structure.id.replaceAll("/", "_");
    const module = getModule(chapterId, moduleId);

    if (contentId && moduleId) {
      const contentKey = cohortCode + "/" + contentId
      const moduleKey = chapterId + "/" + moduleId;
      if (visited) { // UX trick - if the slide is visited, mark it right away in the local state
        markSlideVisited(contentKey, moduleKey, slideId, visited);
      }
      try {
        if (slideId==="_START" || slideId==="_END") { // We only mark the start/end slide as visited (for the whole modules/slideset)
          const resp = await api.markSlideVisited(
            setUnusedApiState,
            cohortCode,
            contentId,
            chapterId,
            moduleId,
            slideId,
            visited
          );
          if (resp.status === "ok") {
            markSlideVisited(contentKey, moduleKey, slideId, visited);
            if (setCodeReview && module?.type?.startsWith(CODEREVIEW) && !visited) { // Then we also remove the lab accomplishment
              CodeReviewSubmitHandler(-1, /*null,*/ setUnusedApiState);
            }
          }
        }
      } catch (e: Response | any) {
      }
    }
  }


  useEffect(() => {
    if (assessmentObject && selectedCourse?.contentData.content_structure.assessments) {
      const questionsId = assessmentObject.questions_id;
      selectedCourse.contentData.content_structure.assessments[questionsId] = assessmentObject;
      if (selectedItem) // Force re-render by updating selectedItem reference
        setSelectedItem({ ...selectedItem });
    }
  }, [assessmentObject]);


  useEffect(() => {
    setAssessmentObject(assessment?.assessment_obj ?? null);
    setQuestions(assessment?.questions ?? null);
    setAnswersData(assessment?.answers_data ?? null);
  }, [assessment]);


  useEffect(() => {
  }, [answersData]);

  useEffect(() => {
    if (getAssessmentApiState.isLoading)
      setMessage(null);
  }, [getAssessmentApiState]);


  function updateAssessment(submitting:boolean, assessment_obj: IAssessmentObject, _answers_data: IAnswersData|null) {
    setAssessmentObject(assessment_obj);
    if (_answers_data) {
      setAnswersData(_answers_data);
    }
    setSubmitting(submitting);
    if (submitting)
      setElapsed(null);
  }

  return <>
    <Loading isLoading={apiState.isLoading}>
      {!!apiState.errorMessage && !apiState.isUnauthenticated ? (
        <ErrorNotification errorMessage={apiState.errorMessage} />
      ) : (
        <>
          {userContent !== null &&
            <SidebarLayout
              keyForStorage="content"
              sidebarClass="sidebar container"
              sidebarContainerClass="content__wrapper"
              minimizedTitle="Lessons"
              hideSidebar={false}
              sidebarContent={
                <div className="flex-column">
                  {userContent.content_data.length > 1 &&
                    <div title={hasPermission(props.userInfo,[{permission:"test"}]) && selectedCourse ? 
                               `Content from the ${selectedCourse.contentData.content_structure.lmodule_code} learning module of the ${selectedCourse.contentData.content_structure.cohort_code} cohort.` : ""}>
                      <h3 className={styles.course__title_selector_label}>Select a course to start</h3>
                      <Select
                        className={`courseSelector`}
                        classNamePrefix="courseSelector"
                        options={userContent.content_data.map((content: IContentData, index) => {return {"label": content.content_structure.title, "value": index};})}
                        onChange={handleChange}
                        value={{"label": userContent.content_data[selectedCourse?.courseIndex??0].content_structure.title, "value": selectedCourse?.courseIndex??0}}
                        defaultValue={{"label": userContent.content_data[0].content_structure.title, "value": 0}}
                        /*menuIsOpen={true}*/
                      />
                    </div>
                  }
                  {
                    userContent.content_data.length === 1 &&
                    <>
                      <h3 className={styles.course__title}>
                        {userContent.content_data[0].content_structure.title}
                      </h3>
                    </>
                  }
                  {
                    userContent.content_data.length === 0 &&
                    <>
                      <h3 className={styles.course__title}>You don't have a course</h3>
                    </>
                  }
                  {!!selectedCourse &&
                    <>
                      {selectedCourse.contentData.content_structure.manual_path && !props.codeReviewOnly &&
                        <div className={styles.resources}>
                          {
                            selectedCourse.contentData.content_structure.manual_watermarking ? <>
                              <p>Your course manual is being prepared. Please check back in a couple of minutes.</p>
                            </> : <>
                              <a href={selectedCourse.contentData.content_structure.manual_path} download>
                                <img src="/static/img/files.svg" alt="Files icon" />
                                Download the course manual in PDF.
                              </a>
                            </>
                          }
                        </div>
                      }
                      <ContentItemSelector
                        selectedLesson={selectedItem?.lessonKey ?? null}
                        courseData={selectedCourse.contentData}
                        visitedSlides={userContent.visitedslides}
                        onSlideVisited={slideVisitedHandler}
                        codeReviewOnly={props.codeReviewOnly}
                        setGetAssessmentApiState={setGetAssessmentApiState}
                        setMessage={setMessage}
                        setAssessment={setAssessment}
                        busy={submitting || getAssessmentApiState.isLoading}
                      />
                    </>
                  }
                </div>
              }
              mainContent={
                <>
                  {message && (
                    <div className={styles.assessmentMessage}>
                      {message}
                    </div>
                  )}
                  {
                    !!selectedCourse &&
                    selectedCourse.courseIndex >= 0 &&
                    selectedCourse.courseIndex < selectedCourse.contentData.content_root.length &&
                    !!selectedItem
                    ? <>
                        { selectedItem.type?.startsWith(PRESENTATION) && !props.codeReviewOnly && selectedItem.module && !selectedItem.module.disabled ?
                          <ContentSlides
                            appInfo={props.appInfo}
                            userId={props.userInfo.user_id}
                            courseData={selectedCourse.contentData}
                            cohortCode={selectedCourse.contentData.content_structure.cohort_code}
                            lmoduleCode={selectedCourse.contentData.content_structure.lmodule_code}
                            vms={selectedCourse.contentData.content_structure.vms}
                            module={selectedItem.module}
                            modulePath={selectedItem.modulePath ?? ""}
                            slides={selectedItem.slides ?? {}}
                            previousLessonLink={computePreviousLessonLink(selectedItem.lessonKey)}
                            nextLessonLink={computeNextLessonLink(selectedItem.lessonKey)}
                            contentStructureId={selectedCourse.contentData.content_structure.id}
                            chapterId={!!selectedItem.chapter ? selectedItem.chapter.id : ""}
                            onSlideVisited={slideVisitedHandler}
                          />
                        : selectedItem.type?.startsWith(CODEREVIEW) && selectedItem.module && !selectedItem.module.disabled ? 
                          <CodeReview
                            appInfo={props.appInfo}
                            userId={props.userInfo.user_id}
                            cohortCode={selectedCourse.contentData.content_structure.cohort_code}
                            contentId={selectedCourse.contentData.content_structure.id}
                            chapterId={selectedItem.chapter?.id ?? ""}
                            module={selectedItem.module}
                            labAccomplishment={labAccomplishment}
                            previousLessonLink={computePreviousLessonLink(selectedItem.lessonKey)}
                            nextLessonLink={computeNextLessonLink(selectedItem.lessonKey)}
                            onSubmitted={CodeReviewSubmitHandler}
                            codeReviewOnly={props.codeReviewOnly}
                            message={codeReviewMessage}
                          />
                        : selectedItem.type?.startsWith(ASSESSMENT) && !props.codeReviewOnly ? 
                          <>
                            <Loading isLoading={getAssessmentApiState.isLoading} className={styles.assessmentLoader}>
                              {!!assessmentObject && !!questions && (
                                <>
                                  {elapsed && !submitting && (assessmentObject.fillout_time??0)>0 && (
                                    <div id="elapsedDiv" className={styles.elapsedIndicator}>
                                      <span id="elapsedText">{getTimeString(elapsed)} (max {assessmentObject.fillout_time} min)</span>
                                    </div>
                                  )}
                                  <Assessment
                                    anonymous={false}
                                    appInfo={props.appInfo}
                                    userInfo={props.userInfo}
                                    assessmentObject={assessmentObject}
                                    questions={questions}
                                    answersData={answersData}
                                    showOnly={false}
                                    allowFilling={true}
                                    allowSubmit={true}
                                    canSave={true}
                                    recaptchaToken={null}
                                    setMessage={setMessage}
                                    setElapsed={(assessmentObject.fillout_time??0) > 0 ? setElapsed : undefined}
                                    updateAssessment={updateAssessment}
                                  />
                                </>
                              )}
                            </Loading>
                          </>
                        :
                          <>
                          </>
                        }
                      </> : <>
                        <div className={styles.content__placeholder}>
                          <div className={styles.text}>Pick a<br />lesson to<br /><b>start</b></div>
                          <img className={styles.svg__child} src="/static/img/child.svg" alt="Child svg" style={{ zIndex: -1 }} />
                          <img className={styles.svg__cloud1} src="/static/img/cloud1.svg" alt="Cloud svg1" style={{ zIndex: -1 }} />
                          <img className={styles.svg__cloud2} src="/static/img/cloud2.svg" alt="Cloud svg2" style={{ zIndex: -1 }} />
                          <img className={styles.svg__circle} src="/static/img/circle.svg" alt="Circle svg" style={{ zIndex: -1 }} />
                        </div>
                      </>
                  }
                </>
              }
            />
          }
        </>
      )}
    </Loading >
  </>;
}
